import React, { useEffect, useRef } from "react";
import { FaHeart, FaRegHeart, FaTimes, FaVideo } from "react-icons/fa";

import {
  DEFAULT_TECHNIQUE_IMAGE,
  getCeintureBadgeClass,
  getTechniqueUv2List,
} from "../utils/techniquesRevisionUtils";

function TechniqueDetailModal({
  technique,
  isFavorite = false,
  onClose,
  onToggleFavorite,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!technique) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [technique, onClose]);

  if (!technique) return null;

  const uv2List = getTechniqueUv2List(technique);

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:p-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-600 dark:text-sky-200">
              Détail technique
            </p>

            <h2 className="mt-2 font-titre text-3xl font-bold text-slate-950 dark:text-white">
              {technique?.nom || "Technique"}
            </h2>

            {technique?.traduction && (
              <p className="mt-1 text-sm font-medium italic text-slate-500 dark:text-white/50">
                {technique.traduction}
              </p>
            )}
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-white/10 dark:text-white"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="grid flex-1 overflow-y-auto lg:grid-cols-[0.75fr_1fr]">
          <div className="bg-slate-100 p-4 dark:bg-slate-950 sm:p-6">
            <div className="mx-auto aspect-[3/4] max-h-[72vh] overflow-hidden rounded-[2rem] bg-white shadow-xl dark:bg-white/[0.03]">
              <img
                src={technique?.image1_url || DEFAULT_TECHNIQUE_IMAGE}
                alt={technique?.nom || "Technique"}
                className="h-full w-full object-contain"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_TECHNIQUE_IMAGE;
                }}
              />
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="flex flex-wrap gap-2">
              {technique?.ceinture && (
                <span
                  className={`rounded-full px-3 py-1 text-xs font-bold ${getCeintureBadgeClass(
                    technique.ceinture,
                  )}`}
                >
                  {technique.ceinture}
                </span>
              )}

              {technique?.famille && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60">
                  {technique.famille}
                </span>
              )}

              {uv2List.map((fiche) => (
                <span
                  key={fiche}
                  className="rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-200"
                >
                  UV2 {fiche}
                </span>
              ))}
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Famille
                </p>

                <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                  {technique?.famille || "Non spécifiée"}
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Ceinture
                </p>

                <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                  {technique?.ceinture || "Non spécifiée"}
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05] sm:col-span-2">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Fiches UV2
                </p>

                <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                  {uv2List.length ? uv2List.join(", ") : "Non concernée"}
                </p>
              </div>
            </div>

            {technique?.information && (
              <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Informations
                </p>

                <p className="mt-2 whitespace-pre-line text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
                  {technique.information}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-3">
          {technique?.video_url && (
            <a
              href={technique.video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5"
            >
              <FaVideo />
              Voir la vidéo
            </a>
          )}

          <button
            type="button"
            onClick={onToggleFavorite}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5"
          >
            {isFavorite ? <FaHeart /> : <FaRegHeart />}
            {isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            <FaTimes />
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default TechniqueDetailModal;